import { Search } from "@/components/ui";
import { useMedHistoryConditionSearch } from "@/features/Charting/network/useMedHistoryConditionSearch";
import { SearchIcon } from "lucide-react";
import { useState } from "react";
import type { CodeSuggestion, SnomedCodeSelection } from "../../network/types";

const SNOMED_SYSTEM = "http://snomed.info/sct";

interface ConditionCodePickerProps {
  suggestions: CodeSuggestion[];
  patientId: string;
  selectedCode: SnomedCodeSelection | null;
  onSelect: (code: SnomedCodeSelection) => void;
}

/**
 * Single combobox for create_condition items. On focus with no input it shows
 * the AI's suggested SNOMED codes; once the user types ≥3 chars it swaps to
 * live useMedHistoryConditionSearch results for a free override search.
 */
export function ConditionCodePicker({
  suggestions,
  patientId,
  selectedCode,
  onSelect,
}: ConditionCodePickerProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const { isLoading, data: searchResults } = useMedHistoryConditionSearch({
    searchTerm,
    patientId,
  });

  const isSearching = searchTerm.length > 2;

  const options = isSearching
    ? (searchResults?.map((r) => ({
        label: r.conditionDisplay,
        value: r.conditionCode,
        metadata: { system: r.conditionSystem },
      })) ?? [])
    : suggestions.map((s) => ({
        label: s.display,
        value: s.code,
        metadata: { system: s.system },
      }));

  return (
    <div className="mt-1">
      <Search
        input={selectedCode?.display ?? ""}
        leftIcon={SearchIcon}
        isLoading={isSearching && isLoading}
        showLoadingSpinner
        options={options}
        placeholder="Search for a condition..."
        shouldFilter={false}
        openOnFocus
        openWithoutInput
        onInputChange={(val) => setSearchTerm(val)}
        onItemSelected={(item) => {
          const system =
            (item.metadata as { system?: string })?.system ?? SNOMED_SYSTEM;
          onSelect({
            code: item.value?.toString() ?? "",
            display: item.label ?? "",
            system,
          });
          setSearchTerm("");
        }}
      />
    </div>
  );
}
